/**
 * CategoryModal - Modal para Criar/Editar Categoria
 * Formulário completo para gestão de categorias com validações e seleção de cores
 */

import React, { useState, useEffect } from 'react';
import {
  X,
  Folder,
  Palette,
  Save,
  Plus
} from 'lucide-react';
import { useTheme } from '../contexts/ThemeContext.tsx';

import {
  CategoryResponse,
  CategoryCreate,
  CategoryUpdate,
  CategoriesService
} from '../services/apiTags.ts';

interface CategoryModalProps {
  category?: CategoryResponse;
  isOpen: boolean;
  onClose: () => void;
  onSave: () => void;
  clinicId: number;
  onCreateCategory?: (name: string) => Promise<CategoryResponse>;
}

const CategoryModal: React.FC<CategoryModalProps> = ({
  category,
  isOpen,
  onClose,
  onSave,
  clinicId,

  onCreateCategory
}) => {
  const { isDark } = useTheme();
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    color: '#128C7E'
  });

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(false);
  const [quickCreate, setQuickCreate] = useState(false);
  const [quickCategoryName, setQuickCategoryName] = useState('');

  // Cores predefinidas (paleta WhatsApp-inspired)
  const colorOptions = [
    '#25D366', // verde WhatsApp
    '#128C7E', // azul WhatsApp
    '#DCF8C6', // verde claro WhatsApp
    '#34B7F1', // azul claro
    '#DC4743', // vermelho
    '#FFA726', // laranja
    '#AB47BC', // roxo
    '#26A69A', // teal
    '#FF7043', // vermelho-laranja
    '#42A5F5', // azul vibrante
    '#66BB6A', // verde suave
    '#EC407A', // rosa
    '#5C6BC0', // índigo
    '#78909C', // azul acinzentado
    '#8D6E63', // marrom
    '#FDD835'  // amarelo
  ];

  // Inicializar formulário quando category mudar
  useEffect(() => {
    if (category) {
      setFormData({
        name: category.name,
        description: category.description || '',
        color: category.color
      });
    } else {
      setFormData({
        name: '',
        description: '',
        color: CategoriesService.generateRandomCategoryColor()
      });
    }
    setErrors({});
    setQuickCreate(false);
  }, [category]);

  // Resetar formulário
  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      color: CategoriesService.generateRandomCategoryColor()
    });
    setErrors({});
    setQuickCategoryName('');
  };

  // Fechar modal
  const handleClose = () => {
    if (!loading) {
      resetForm();
      onClose();
    }
  };

  // Validar formulário
  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Nome da categoria é obrigatório';
    } else if (formData.name.trim().length < 2) {
      newErrors.name = 'Nome deve ter pelo menos 2 caracteres';
    } else if (formData.name.trim().length > 50) {
      newErrors.name = 'Nome deve ter no máximo 50 caracteres';
    }

    if (formData.description && formData.description.length > 300) {
      newErrors.description = 'Descrição deve ter no máximo 300 caracteres';
    }

    if (!CategoriesService.isValidHexColor(formData.color)) {
      newErrors.color = 'Cor inválida. Use formato #RRGGBB';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Salvar categoria
  const handleSave = async (e?: React.FormEvent) => {
    if (e) {
      e.preventDefault();
    }

    if (!validateForm()) {
      return;
    }

    try {
      setLoading(true);

      if (category) {
        // Editar categoria existente
        const updateData: CategoryUpdate = {
          name: formData.name.trim(),
          description: formData.description.trim() || undefined,
          color: formData.color
        };

        await CategoriesService.updateCategory(category.id, updateData);
        console.log(`✅ Categoria "${formData.name}" atualizada com sucesso`);
      } else {
        // Criar nova categoria
        const createData: CategoryCreate = {
          name: formData.name.trim(),
          description: formData.description.trim() || undefined,
          color: formData.color
        };

        await CategoriesService.createCategory(clinicId, createData);
        console.log(`✅ Categoria "${formData.name}" criada com sucesso`);
      }

      resetForm();
      onSave();
    } catch (error: any) {
      console.error('❌ Erro ao salvar categoria:', error);

      if (error.response?.data?.detail) {
        setErrors({ general: error.response.data.detail });
      } else {
        setErrors({ general: 'Erro ao salvar categoria. Tente novamente.' });
      }
    } finally {
      setLoading(false);
    }
  };

  // Quick create
  const handleQuickCreate = async () => {
    if (!quickCategoryName.trim()) {
      setErrors({ quickName: 'Nome da categoria é obrigatório' });
      return;
    }

    try {
      setLoading(true);
      if (onCreateCategory) {
        await onCreateCategory(quickCategoryName.trim());
      }
      setQuickCategoryName('');
      setQuickCreate(false);
      onSave();
    } catch (error: any) {
      console.error('❌ Erro ao criar categoria rápida:', error);
      if (error.response?.data?.detail) {
        setErrors({ quickName: error.response.data.detail });
      } else {
        setErrors({ quickName: 'Erro ao criar categoria' });
      }
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className={`rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto transition-all ${isDark ? 'bg-gray-800' : 'bg-white'
        }`}>
        {/* Header */}
        <div className={`flex items-center justify-between p-6 border-b ${isDark ? 'border-gray-700' : 'border-gray-100'}`}>
          <div className="flex items-center gap-3">
            <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${isDark ? 'bg-sminy-primary/20 text-sminy-primary' : 'bg-sminy-primary/10 text-sminy-primary'
              }`}>
              <Folder className="w-5 h-5" />
            </div>
            <div>
              <h3 className={`text-xl font-semibold ${isDark ? 'text-white' : 'text-slate-900'}`}>
                {category ? 'Editar Categoria' : 'Nova Categoria'}
              </h3>
              <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-slate-500'}`}>
                {category ? 'Atualize as informações da categoria' : 'Preencha os dados para criar uma nova categoria'}
              </p>
            </div>
          </div>
          <button
            onClick={handleClose}
            className={`p-2 transition-colors rounded-lg ${isDark ? 'text-gray-400 hover:text-gray-200 hover:bg-gray-700' : 'text-slate-400 hover:text-slate-600 hover:bg-slate-100'
              }`}
            disabled={loading}
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Quick Create Toggle */}
        {!category && (
          <div className={`p-6 border-b ${isDark ? 'border-gray-700' : 'border-gray-100'}`}>
            <button
              onClick={() => setQuickCreate(!quickCreate)}
              className={`text-sm font-medium flex items-center gap-2 ${isDark ? 'text-sminy-primary hover:text-sminy-primary-hover' : 'text-sminy-primary hover:text-sminy-primary-hover'
                }`}
            >
              <Plus className="w-4 h-4" />
              Criação Rápida
            </button>
          </div>
        )}

        {/* Quick Create Form */}
        {quickCreate && !category && (
          <div className={`p-6 border-b ${isDark ? 'border-gray-700 bg-gray-800/50' : 'border-gray-100 bg-sminy-primary/5'}`}>
            <div className="space-y-4">
              <div>
                <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-gray-300' : 'text-slate-700'}`}>
                  Nome da Categoria
                </label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={quickCategoryName}
                    onChange={(e) => setQuickCategoryName(e.target.value)}
                    className={`flex-1 px-4 py-2.5 rounded-xl border outline-none transition-all ${isDark
                      ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-500 focus:border-sminy-primary'
                      : 'bg-white border-slate-200 text-slate-900 placeholder-slate-400 focus:border-sminy-primary focus:ring-2 focus:ring-sminy-primary/20'
                      }`}
                    placeholder="Digite o nome da categoria..."
                  />
                  <button
                    onClick={handleQuickCreate}
                    disabled={loading || !quickCategoryName.trim()}
                    className="px-4 py-2 bg-sminy-primary text-white rounded-xl hover:bg-sminy-primary-hover disabled:opacity-50 disabled:cursor-not-allowed shadow-sm shadow-sminy-primary/20"
                  >
                    {loading ? 'Criando...' : 'Criar'}
                  </button>
                </div>
                {errors.quickName && (
                  <p className="text-sm text-red-500 mt-1">{errors.quickName}</p>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Formulário Principal */}
        {!quickCreate && (
          <form onSubmit={handleSave} className="p-6 space-y-6">
            {/* Erro Geral */}
            {errors.general && (
              <div className={`border rounded-lg p-4 ${isDark ? 'bg-red-900/20 border-red-800 text-red-400' : 'bg-red-50 border-red-200 text-red-600'}`}>
                {errors.general}
              </div>
            )}

            {/* Nome */}
            <div>
              <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-gray-300' : 'text-slate-700'}`}>
                Nome da Categoria <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className={`w-full px-4 py-2.5 rounded-xl border outline-none transition-all ${isDark
                  ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-500 focus:border-sminy-primary'
                  : 'bg-white border-slate-200 text-slate-900 placeholder-slate-400 focus:border-sminy-primary focus:ring-2 focus:ring-sminy-primary/20'
                  }`}
                placeholder="Ex: Pacientes VIP, Em Tratamento..."
                maxLength={50}
              />
              {errors.name && (
                <p className="text-sm text-red-500 mt-1">{errors.name}</p>
              )}
            </div>

            {/* Descrição */}
            <div>
              <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-gray-300' : 'text-slate-700'}`}>
                Descrição
              </label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                rows={3}
                className={`w-full px-4 py-2.5 rounded-xl border outline-none transition-all resize-none ${isDark
                  ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-500 focus:border-sminy-primary'
                  : 'bg-white border-slate-200 text-slate-900 placeholder-slate-400 focus:border-sminy-primary focus:ring-2 focus:ring-sminy-primary/20'
                  }`}
                placeholder="Descrição opcional da categoria..."
                maxLength={300}
              />
              {errors.description && (
                <p className="text-sm text-red-500 mt-1">{errors.description}</p>
              )}
            </div>

            {/* Cor */}
            <div>
              <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-gray-300' : 'text-slate-700'}`}>
                Cor da Categoria <span className="text-red-500">*</span>
              </label>

              <div className="space-y-3">
                {/* Preview */}
                <div className={`flex items-center gap-3 p-3 rounded-lg ${isDark ? 'bg-gray-700' : 'bg-gray-50'}`}>
                  <div
                    className="w-8 h-8 rounded-full shadow-sm"
                    style={{ backgroundColor: formData.color }}
                  />
                  <span className={`font-mono text-sm ${isDark ? 'text-gray-300' : 'text-slate-600'}`}>{formData.color}</span>
                </div>

                {/* Paleta de Cores */}
                <div className="grid grid-cols-8 gap-2">
                  {colorOptions.map((color) => (
                    <button
                      key={color}
                      type="button"
                      onClick={() => setFormData({ ...formData, color })}
                      className={`w-full h-10 rounded-lg border-2 transition-all ${formData.color === color
                        ? 'border-sminy-primary ring-2 ring-sminy-primary/30'
                        : isDark ? 'border-gray-600 hover:border-gray-500' : 'border-slate-200 hover:border-slate-300'
                        }`}
                      style={{ backgroundColor: color }}
                      title={color}
                    />
                  ))}
                </div>

                {/* Custom Color */}
                <div className="flex items-center gap-2">
                  <Palette className={`w-4 h-4 ${isDark ? 'text-gray-500' : 'text-slate-400'}`} />
                  <input
                    type="text"
                    value={formData.color}
                    onChange={(e) => setFormData({ ...formData, color: e.target.value })}
                    className={`flex-1 px-4 py-2.5 rounded-xl border outline-none font-mono text-sm transition-all ${isDark
                      ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-500 focus:border-sminy-primary'
                      : 'bg-white border-slate-200 text-slate-900 placeholder-slate-400 focus:border-sminy-primary focus:ring-2 focus:ring-sminy-primary/20'
                      }`}
                    placeholder="#RRGGBB"
                  />
                </div>

                {errors.color && (
                  <p className="text-sm text-red-500 mt-1">{errors.color}</p>
                )}
              </div>
            </div>
          </form>
        )}

        {/* Footer */}
        {!quickCreate && (
          <div className={`flex items-center justify-end gap-3 p-6 border-t rounded-b-xl ${isDark ? 'bg-gray-800/50 border-gray-700' : 'bg-gray-50 border-gray-100'}`}>
            <button
              type="button"
              onClick={handleClose}
              className={`px-4 py-2 transition-colors rounded-lg ${isDark ? 'text-gray-400 hover:text-gray-200 hover:bg-gray-700' : 'text-slate-600 hover:text-slate-800 hover:bg-slate-200'}`}
              disabled={loading}
            >
              Cancelar
            </button>
            <button
              type="submit"
              onClick={handleSave}
              disabled={loading}
              className="px-6 py-2 bg-sminy-primary text-white rounded-xl hover:bg-sminy-primary-hover disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 shadow-sm shadow-sminy-primary/20"
            >
              {loading ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  Salvando...
                </>
              ) : (
                <>
                  <Save className="w-4 h-4" />
                  {category ? 'Atualizar' : 'Criar'}
                </>
              )}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default CategoryModal;